import {
  MotorDataType,
  OptimizationParameterValue,
  ScheduleConstraint,
  ScheduleConstraintType,
} from '@e2m-customer-portal/domains';
import { Nullable } from '@e2m-customer-portal/shared';
import { InfoTooltip, NumberInput, TextInput, ValidatedContainer } from '@e2m-customer-portal/ui';
import { faker } from '@faker-js/faker/locale/en';
import { Button } from '@mui/material';
import { equals } from 'ramda';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface MotorProps {
  data: MotorDataType;
  identifier: string;
  setUpdatedItemsData: Dispatch<SetStateAction<Map<string, Map<string, OptimizationParameterValue>>>>;
  updatedItemsData: Map<string, Map<string, OptimizationParameterValue>> | undefined;
  setSaveBarOpen: Dispatch<SetStateAction<boolean>>;
  setIsValid: Dispatch<SetStateAction<boolean>>;
  resetTrigger?: boolean;
  readOnly: (parameter?: string) => boolean;
}

export function Motor({
  data,
  identifier,
  updatedItemsData,
  setUpdatedItemsData,
  setSaveBarOpen,
  setIsValid,
  resetTrigger,
  readOnly,
}: MotorProps) {
  const [t] = useTranslation();

  const inputClass = 'w-full min-w-4';

  const [updatedData, setUpdatedData] = useState<MotorDataType>(data);
  const [changes, setChanges] = useState<Map<string, OptimizationParameterValue>>(new Map());
  const [scheduleConstraints, setScheduleConstraints] = useState<ScheduleConstraintType[] | undefined>(
    data.schedule_constraints,
  );

  const resetValues = () => {
    setUpdatedData(data);
    setScheduleConstraints(data.schedule_constraints);
    setSaveBarOpen(false);
    setChanges(new Map());
  };

  useEffect(() => {
    if (scheduleConstraints && updatedItemsData && !equals(updatedData.schedule_constraints, scheduleConstraints)) {
      setUpdatedData({ ...updatedData, schedule_constraints: scheduleConstraints });
      changes.set('schedule_constraints', scheduleConstraints);
      setUpdatedItemsData(updatedItemsData.set(identifier, changes));
    }
  }, [scheduleConstraints]);

  useEffect(() => {
    if (changes && updatedItemsData) {
      setUpdatedItemsData(updatedItemsData.set(identifier, changes));
    }
  }, [changes]);

  useEffect(() => {
    if (updatedItemsData?.size === 0) {
      setChanges(new Map());
    }
  }, [updatedItemsData]);

  useEffect(resetValues, [resetTrigger]);

  return (
    <div className="h-fit p-6 grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4 border-solid border border-divider rounded-md">
      <h3 className="m-0 flex flex-col whitespace-nowrap xl:col-span-2">
        <span className="text-xs font-normal text-grey-300">{t('DEVICE_TABLE_HEADER_TECHNICAL_UNIT')}</span>
        <span className="pt-1 text-lg font-medium">{data.alias}</span>
      </h3>
      <ValidatedContainer validate="on" onValidated={setIsValid}>
        <div className="xl:col-span-2">
          {updatedData.target_rated_power_group !== undefined && (
            <TextInput
              value={updatedData.target_rated_power_group}
              name="name"
              className="w-full bg-white"
              label={t('COPT_MOTORS_TARGET_RATED_POWER_GROUP')}
              disabled
              endAdornment={<InfoTooltip field="target_rated_power_group" objectType="motors" />}
            />
          )}
        </div>
        {updatedData.efficiency !== undefined && (
          <NumberInput
            value={updatedData.efficiency === null ? null : Math.round(updatedData.efficiency * 100)}
            className={inputClass}
            required
            name="efficiency"
            label={t('COPT_MOTORS_EFFICIENCY')}
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const efficiency = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
              setUpdatedData({
                ...updatedData,
                efficiency,
              });
              setChanges(changes.set('efficiency', efficiency));
            }}
            disabled={readOnly('efficiency')}
            minValue={30}
            maxValue={80}
            endAdornment={<InfoTooltip field="efficiency" objectType="motors" />}
          />
        )}
        {updatedData.minimal_power_conversion_efficiency !== undefined && (
          <NumberInput
            value={
              updatedData.minimal_power_conversion_efficiency === null
                ? null
                : Math.round(updatedData.minimal_power_conversion_efficiency * 100)
            }
            className={inputClass}
            name="minimal_power_conversion_efficiency"
            label={t('COPT_MOTORS_MINIMAL_POWER_CONVERSION_EFFICIENCY')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const minimal_power_conversion_efficiency =
                value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
              setUpdatedData({
                ...updatedData,
                minimal_power_conversion_efficiency,
              });
              setChanges(changes.set('minimal_power_conversion_efficiency', minimal_power_conversion_efficiency));
            }}
            disabled={readOnly('minimal_power_conversion_efficiency')}
            minValue={30}
            maxValue={80}
            endAdornment={<InfoTooltip field="minimal_power_conversion_efficiency" objectType="motors" />}
          />
        )}
        {updatedData.p_min !== undefined && updatedData.p_max !== undefined && (
          <h4 className="m-0 pt-3 border-solid border-0 border-t border-divider text-sm font-semibold xl:col-span-2">
            {t('SHARED_FORECAST')}
          </h4>
        )}
        {updatedData.p_min !== undefined && updatedData.p_max !== undefined && (
          <NumberInput
            value={updatedData.p_min === null ? null : updatedData.p_min * 1000}
            className={inputClass}
            name="p_min"
            label={t('COPT_MOTORS_P_MIN')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const p_min = value != null && !Number.isNaN(value) ? value / 1000 : null;
              setUpdatedData({
                ...updatedData,
                p_min,
              });
              setChanges(changes.set('p_min', p_min));
            }}
            disabled={readOnly('p_min')}
            notNegative
            maxValue={updatedData.p_max! * 1000}
            endAdornment={<InfoTooltip field="p_min" objectType="motors" />}
          />
        )}
        {updatedData.p_max !== undefined && updatedData.p_min !== undefined && (
          <NumberInput
            value={updatedData.p_max === null ? null : updatedData.p_max * 1000}
            className={inputClass}
            name="p_max"
            label={t('COPT_MOTORS_P_MAX')}
            required
            onChange={(value: number) => {
              setSaveBarOpen(true);
              const p_max = value != null && !Number.isNaN(value) ? value / 1000 : null;
              setUpdatedData({
                ...updatedData,
                p_max,
              });
              setChanges(changes.set('p_max', p_max));
            }}
            disabled={readOnly('p_max')}
            minValue={updatedData.p_min! * 1000}
            endAdornment={<InfoTooltip field="p_max" objectType="motors" />}
          />
        )}
        {scheduleConstraints !== undefined && (
          <div className="pt-3 flex justify-between border-solid border-0 border-t border-divider xl:col-span-2">
            <h4 className="m-0 flex items-center text-sm font-semibold">
              {t('COPT_MOTORS_SCHEDULE_CONSTRAINTS')}
              <InfoTooltip text={t('COPT_MOTORS_SCHEDULE_CONSTRAINTS_TOOLTIP')} field="schedule_constraints" />
            </h4>
            <Button
              color="primary"
              disabled={readOnly('schedule_constraints')}
              className="text-2xl flex h-10 w-10 min-w-10 shrink-0 grow-0 items-center justify-center rounded-full"
              onClick={() => {
                setSaveBarOpen(true);
                setScheduleConstraints([
                  ...scheduleConstraints,

                  {
                    identifier: faker.string.uuid(),
                    start_time: '00:00',
                    end_time: '00:00',
                    days_of_week: [],
                    minimum_power: 0,
                    maximum_power: 0,
                  },
                ]);
              }}
            >
              +
            </Button>
          </div>
        )}
        {scheduleConstraints?.map((constraint) => {
          return (
            <div className="xl:col-span-2" key={constraint.identifier}>
              <ScheduleConstraint
                data={constraint}
                scheduleConstraints={scheduleConstraints}
                setScheduleConstrains={setScheduleConstraints}
                setSaveBarOpen={setSaveBarOpen}
                readOnly={readOnly}
                setIsValid={setIsValid}
              />
            </div>
          );
        })}
        {updatedData.always_available !== undefined && (
          <label className="flex items-center text-sm font-light mr-4 xl:col-span-2">
            <input
              type="checkbox"
              checked={!!updatedData.always_available}
              disabled={readOnly('always_available')}
              onChange={() => {
                setSaveBarOpen(true);
                const always_available = !updatedData.always_available;
                setUpdatedData({ ...updatedData, always_available });
                setChanges(changes.set('always_available', always_available));
              }}
              className="w-4 h-4 mr-2 accent-green-200"
            />
            {t('COPT_MOTORS_ALWAYS_AVAILABLE')}
            <InfoTooltip field="always_available" objectType="motors" />
          </label>
        )}
        {(updatedData.on_time_min !== undefined || updatedData.on_time_max !== undefined) && (
          <h4 className="m-0 pt-3 text-sm font-semibold border-solid border-0 border-t border-divider xl:col-span-2">
            {t('COPT_MOTORS_ON_TIME')}
          </h4>
        )}
        {updatedData.on_time_min !== undefined && (
          <NumberInput
            value={updatedData.on_time_min}
            className={inputClass}
            name="on_time_min"
            label={t('COPT_MOTORS_ON_TIME_MIN')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const on_time_min = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                on_time_min,
              });
              setChanges(changes.set('on_time_min', on_time_min));
            }}
            disabled={readOnly('on_time_min')}
            notNegative
            endAdornment={<InfoTooltip field="on_time_min" objectType="motors" />}
          />
        )}
        {updatedData.on_time_max !== undefined && (
          <NumberInput
            value={updatedData.on_time_max}
            className={inputClass}
            name="on_time_max"
            label={t('COPT_MOTORS_ON_TIME_MAX')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const on_time_max = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                on_time_max,
              });
              setChanges(changes.set('on_time_max', on_time_max));
            }}
            disabled={readOnly('on_time_max')}
            notNegative
            endAdornment={<InfoTooltip field="on_time_max" objectType="motors" />}
          />
        )}
        {(updatedData.off_time_min !== undefined || updatedData.off_time_max !== undefined) && (
          <h4 className="m-0 pt-3 text-sm font-semibold border-solid border-0 border-t border-divider xl:col-span-2">
            {t('COPT_MOTORS_OFF_TIME')}
          </h4>
        )}
        {updatedData.off_time_min !== undefined && (
          <NumberInput
            value={updatedData.off_time_min}
            className={inputClass}
            name="off_time_min"
            label={t('COPT_MOTORS_OFF_TIME_MIN')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const off_time_min = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                off_time_min,
              });
              setChanges(changes.set('off_time_min', off_time_min));
            }}
            disabled={readOnly('off_time_min')}
            notNegative
            endAdornment={<InfoTooltip field="off_time_min" objectType="motors" />}
          />
        )}
        {updatedData.off_time_max !== undefined && (
          <NumberInput
            value={updatedData.off_time_max}
            className={inputClass}
            name="off_time_max"
            label={t('COPT_MOTORS_OFF_TIME_MAX')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const off_time_max = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                off_time_max,
              });
              setChanges(changes.set('off_time_max', off_time_max));
            }}
            disabled={readOnly('off_time_max')}
            notNegative
            endAdornment={<InfoTooltip field="off_time_max" objectType="motors" />}
          />
        )}
        {(updatedData.daily_on_time_min !== undefined || updatedData.daily_on_time_max !== undefined) && (
          <h4 className="m-0 pt-3 text-sm font-semibold border-solid border-0 border-t border-divider xl:col-span-2">
            {t('COPT_MOTORS_DAILY_ON_TIME')}
          </h4>
        )}
        {updatedData.daily_on_time_min !== undefined && (
          <NumberInput
            value={updatedData.daily_on_time_min}
            className={inputClass}
            name="daily_on_time_min"
            label={t('COPT_MOTORS_DAILY_ON_TIME_MIN')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const daily_on_time_min = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                daily_on_time_min,
              });
              setChanges(changes.set('daily_on_time_min', daily_on_time_min));
            }}
            disabled={readOnly('daily_on_time_min')}
            notNegative
            endAdornment={<InfoTooltip field="daily_on_time_min" objectType="motors" />}
          />
        )}
        {updatedData.daily_on_time_max !== undefined && (
          <NumberInput
            value={updatedData.daily_on_time_max}
            className={inputClass}
            name="daily_on_time_max"
            label={t('COPT_MOTORS_DAILY_ON_TIME_MAX')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const daily_on_time_max = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                daily_on_time_max,
              });
              setChanges(changes.set('daily_on_time_max', daily_on_time_max));
            }}
            disabled={readOnly('daily_on_time_max')}
            notNegative
            endAdornment={<InfoTooltip field="daily_on_time_max" objectType="motors" />}
          />
        )}
        {(updatedData.daily_starts_min !== undefined || updatedData.daily_starts_max !== undefined) && (
          <h4 className="m-0 pt-3 text-sm font-semibold border-solid border-0 border-t border-divider xl:col-span-2">
            {t('COPT_MOTORS_DAILY_START')}
          </h4>
        )}
        {updatedData.daily_starts_min !== undefined && (
          <NumberInput
            value={updatedData.daily_starts_min}
            className={inputClass}
            name="daily_starts_min"
            label={t('COPT_MOTORS_DAILY_STARTS_MIN')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const daily_starts_min = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                daily_starts_min,
              });
              setChanges(changes.set('daily_starts_min', daily_starts_min));
            }}
            disabled={readOnly('daily_starts_min')}
            notNegative
            endAdornment={<InfoTooltip field="daily_starts_min" objectType="motors" />}
          />
        )}
        {updatedData.daily_starts_max !== undefined && (
          <NumberInput
            value={updatedData.daily_starts_max}
            className={inputClass}
            name="daily_starts_max"
            label={t('COPT_MOTORS_DAILY_STARTS_MAX')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const daily_starts_max = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                daily_starts_max,
              });
              setChanges(changes.set('daily_starts_max', daily_starts_max));
            }}
            disabled={readOnly('daily_starts_max')}
            notNegative
            endAdornment={<InfoTooltip field="daily_starts_max" objectType="motors" />}
          />
        )}
        {(updatedData.weekly_starts_min !== undefined || updatedData.weekly_starts_max !== undefined) && (
          <h4 className="m-0 pt-3 text-sm font-semibold border-solid border-0 border-t border-divider xl:col-span-2">
            {t('COPT_MOTORS_WEEKLY_START')}
          </h4>
        )}
        {updatedData.weekly_starts_min !== undefined && (
          <NumberInput
            value={updatedData.weekly_starts_min}
            className={inputClass}
            name="weekly_starts_min"
            label={t('COPT_MOTORS_WEEKLY_STARTS_MIN')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const weekly_starts_min = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                weekly_starts_min,
              });
              setChanges(changes.set('weekly_starts_min', weekly_starts_min));
            }}
            disabled={readOnly('weekly_starts_min')}
            notNegative
            endAdornment={<InfoTooltip field="weekly_starts_min" objectType="motors" />}
          />
        )}
        {updatedData.weekly_starts_max !== undefined && (
          <NumberInput
            value={updatedData.weekly_starts_max}
            className={inputClass}
            name="weekly_starts_max"
            label={t('COPT_MOTORS_WEEKLY_STARTS_MAX')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const weekly_starts_max = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                weekly_starts_max,
              });
              setChanges(changes.set('weekly_starts_max', weekly_starts_max));
            }}
            disabled={readOnly('weekly_starts_max')}
            notNegative
            endAdornment={<InfoTooltip field="weekly_starts_max" objectType="motors" />}
          />
        )}
        {(updatedData.start_costs !== undefined || updatedData.additional_start_costs !== undefined) && (
          <h4 className="m-0 pt-3 text-sm font-semibold border-solid border-0 border-t border-divider xl:col-span-2">
            {t('COPT_MOTORS_COSTS')}
          </h4>
        )}
        {updatedData.start_costs !== undefined && (
          <NumberInput
            value={updatedData.start_costs}
            className={inputClass}
            name="start_costs"
            label={t('COPT_MOTORS_START_COSTS')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const start_costs = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                start_costs,
              });
              setChanges(changes.set('start_costs', start_costs));
            }}
            disabled={readOnly('start_costs')}
            notNegative
            endAdornment={<InfoTooltip field="start_costs" objectType="motors" />}
          />
        )}
        {updatedData.additional_start_costs !== undefined && (
          <NumberInput
            value={updatedData.additional_start_costs}
            className={inputClass}
            name="additional_start_costs"
            label={t('COPT_MOTORS_ADDITIONAL_START_COSTS')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              const additional_start_costs = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                additional_start_costs,
              });
              setChanges(changes.set('additional_start_costs', additional_start_costs));
            }}
            disabled={readOnly('additional_start_costs')}
            notNegative
            endAdornment={<InfoTooltip field="additional_start_costs" objectType="motors" />}
          />
        )}
      </ValidatedContainer>
    </div>
  );
}
